export type UserRole = 'writer' | 'authorizer' | 'admin';

export interface UserAccount {
  id: string;
  email: string;
  name: string;
  title: string;
  role: UserRole;
  avatar: string;
  department: string;
  status?: 'active' | 'suspended';
  password?: string;
  createdAt?: string;
  lastLogin?: string;
  mustChangePassword?: boolean;
  initialLoginCompleted?: boolean;
  passwordLastChanged?: string;
  allowedRestrictedAreas?: string[];
  forcePasswordResetReason?: string;
}

export type DocumentApprovalStatus = 'draft' | 'pending_approval' | 'changes_requested' | 'approved_sealed';
export type DocTag = 'Confidential' | 'Urgent' | 'Archived' | 'None';

export interface Account {
  code: string; // XX-XXX-XXX
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  description?: string;
}

export const CHART_OF_ACCOUNTS: Account[] = [
  // ASSETS (10-series)
  { code: '10-100-001', name: 'First Bank - Festival Main Operating Account', type: 'asset', description: 'Primary bank account for festival revenues and disbursements' },
  { code: '10-100-002', name: 'Zenith Bank - ERUEWin Raffle Vault Account', type: 'asset', description: 'Dedicated escrow account for raffle ticket sales and prize payouts' },
  { code: '10-100-003', name: 'Petty Cash Custody Vault', type: 'asset', description: 'Imprest cash held by Financial Scribe for immediate ground expenses' },
  { code: '10-200-001', name: 'Outstanding Royal & Diaspora Pledges Receivable', type: 'asset', description: 'Pledges made by chiefs, chapters & corporate sponsors awaiting retirement' },
  { code: '10-300-001', name: 'Festival Stage, Sound & Lighting Equipment Assets', type: 'asset', description: 'Capital equipment owned by Uzairue Royal Foundation' },

  // LIABILITIES (20-series)
  { code: '20-100-001', name: 'Vendor Accounts Payable', type: 'liability', description: 'Unpaid vendor invoices for logistics, catering, and security' },
  { code: '20-200-001', name: 'Unearned Online Sponsorship Income', type: 'liability', description: 'Advance sponsorship funds pending post-event delivery' },

  // EQUITY / NET RESERVES (30-series)
  { code: '30-100-001', name: 'General Festival Reserve Fund', type: 'equity', description: 'Accumulated net surplus held for future festival editions' },
  { code: '30-200-001', name: 'Uzairue Youth Skill & Healthcare Endowment Fund', type: 'equity', description: 'Dedicated endowment fund powered by ERUEWin raffle net proceeds' },

  // REVENUES (40-series)
  { code: '40-100-001', name: 'Royal & Diaspora Philanthropic Donations', type: 'revenue', description: 'Direct cash & bank transfer donations from sons & daughters of Uzairue' },
  { code: '40-100-002', name: 'ERUEWin Raffle Hub Sales Revenue', type: 'revenue', description: 'Proceeds from online & ticket hub raffle ticket purchases' },
  { code: '40-100-003', name: 'Retired & Fulfilled Pledges Income', type: 'revenue', description: 'Pledges converted to cash/bank deposits upon retirement' },
  { code: '40-100-004', name: 'Paystack Festival Store & Merchandise Sales', type: 'revenue', description: 'Revenue from sales of royal attire, caps, souvenirs & books' },
  { code: '40-100-005', name: 'Corporate Sponsorships & Exhibition Stalls', type: 'revenue', description: 'Brand sponsorship fees & commercial vendor booth allocations' },

  // EXPENSES (50-series)
  { code: '50-100-001', name: 'Stage Setup, Sound Engineering & Lighting Logistics', type: 'expense', description: 'Stage construction, public address systems & lighting rigs' },
  { code: '50-100-002', name: 'Cultural Troupes & Artiste Honoraria', type: 'expense', description: 'Grants and honoraria paid to 22 village dance troupes & performers' },
  { code: '50-100-003', name: 'Security Escorts, Vigilante Allowances & Emergency Medical', type: 'expense', description: 'Security personnel, traffic wardens & standby medical corps' },
  { code: '50-100-004', name: 'Publicity, Gazette Printing & Royal Media Broadcasting', type: 'expense', description: 'Print programs, banners, radio announcements & digital media' },
  { code: '50-100-005', name: 'Royal Hospitality, VIP Catering & Protocol Feasts', type: 'expense', description: 'Palace receptions, visiting dignitaries & traditional feasts' },
  { code: '50-100-006', name: 'ERUEWin Raffle Star Prize Procurement & Administration', type: 'expense', description: 'SUV procurement, solar system kits, cash prizes & licensing' },
  { code: '50-100-007', name: 'Administrative, Secretarial & Bank Charge Expenses', type: 'expense', description: 'Stationery, portal hosting, audit fees & bank transfer fees' },
];

export interface FinancialTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'revenue' | 'expense' | 'donation' | 'pledge_redemption' | 'store_sale' | 'raffle_sale';
  category: string;
  accountCode: string;
  status: 'pending' | 'cleared' | 'reversed';
  referenceNo?: string;
  payerOrPayee?: string;
  paymentMethod?: 'bank_transfer' | 'paystack_card' | 'cash' | 'pos' | 'cheque';
  voucherNo?: string;
  createdBy?: string;
  approvedBy?: string;
  notes?: string;
}

export interface FestivalPledge {
  id: string;
  pledgerName: string;
  titleOrOffice: string;
  villageOrChapter: string;
  pledgerType: 'royal_chief' | 'diaspora_chapter' | 'corporate_sponsor' | 'individual' | 'government';
  amountPledged: number;
  amountRedeemed: number;
  status: 'outstanding' | 'partially_redeemed' | 'fully_retired';
  pledgeDate: string;
  dueDate?: string;
  purpose: string;
  contactEmail?: string;
  contactPhone?: string;
  redemptionHistory: {
    id: string;
    amount: number;
    date: string;
    paymentMethod: string;
    receiptNo: string;
    recordedBy: string;
    bankRef?: string;
  }[];
}

export interface ExpenseRequisition {
  id: string;
  requisitionNo: string;
  title: string;
  department: 'Logistics & Stage' | 'Cultural Troupes' | 'Security & Health' | 'Media & Publicity' | 'Royal Protocol' | 'Raffle Admin' | 'General Secretarial';
  requestedBy: string;
  amount: number;
  description: string;
  accountCode: string;
  status: 'draft' | 'submitted' | 'mc_reviewed' | 'bot_approved' | 'disbursed' | 'rejected';
  priority: 'normal' | 'urgent';
  requiresBotApproval: boolean; // required if amount > N500,000 or capital spend
  createdDate: string;
  paymentVoucherNo?: string;
  disbursedDate?: string;
  bankAccountDetails?: string;
  beneficiaryName?: string;
  approvalHistory: {
    id: string;
    step: 'Submission' | 'Management Review' | 'Board of Trustees Approval' | 'Disbursement';
    actorName: string;
    actorRole: string;
    action: 'submitted' | 'reviewed' | 'approved' | 'rejected' | 'disbursed';
    timestamp: string;
    notes?: string;
  }[];
}

export interface DepartmentBudget {
  code: string;
  departmentName: string;
  allocatedAmount: number;
  spentAmount: number;
  committedAmount: number; // approved requisitions awaiting payout
}

export interface FinancialAsset {
  id: string;
  name: string;
  value: number;
  acquiredDate: string;
  accountCode: string;
  condition?: 'excellent' | 'good' | 'fair' | 'needs_maintenance';
  location?: string;
}

export interface ApprovalHistoryLog {
  id: string;
  action: 'submitted' | 'approved' | 'changes_requested' | 'sealed' | 'authorized_sealed';
  performedBy?: string;
  actorName?: string;
  actorRole?: string;
  role?: string;
  timestamp: string;
  notes?: string;
}

export interface DocumentWorkflowState {
  status: DocumentApprovalStatus;
  isSealed: boolean;
  sealTimestamp?: string;
  sealRefNo?: string;
  submittedBy?: {
    name: string;
    email: string;
    timestamp: string;
  };
  approvedBy?: {
    name: string;
    title: string;
    timestamp: string;
    sealRefNo: string;
  };
  rejectionNotes?: string;
  history: ApprovalHistoryLog[];
}

export type DocType = 'letterhead' | 'invitation' | 'complimentary' | 'poster' | 'certificate' | 'memo' | 'program' | 'proclamation';
export type MarginPreset = 'normal' | 'narrow' | 'wide' | 'custom';
export type TemplateId = 'classic' | 'modern' | 'royal-gold' | 'minimalist' | 'sidebar';
export type ColorThemeId = 'forest-gold' | 'royal-blue' | 'imperial-burgundy' | 'charcoal' | 'monarch-purple' | 'emerald-jade';
export type FontPreset = 'serif' | 'sans' | 'mixed';
export type SignatureType = 'typed' | 'drawn' | 'scanned' | 'none';

export type RoyalStyleCategory = 'all' | 'ceremonial' | 'official' | 'casual';
export type PageNumberStyle = 'classic' | 'folio' | 'bracket' | 'simple' | 'none';
export type BorderStyleId = 'ornate' | 'classic-double' | 'solid-thin' | 'minimal-dashed' | 'gold-embossed' | 'edo-weave' | 'royal-rosette' | 'diamond-lattice' | 'crown-filigree';

export interface ColorTheme {
  id: ColorThemeId;
  name: string;
  primary: string; // Tailwind class
  primaryHex: string;
  secondary: string;
  secondaryHex: string;
  accent: string;
  accentHex: string;
  lightBg: string;
}

export interface SocialHandles {
  facebook: string;
  youtube: string;
  twitter: string;
  tiktok: string;
  instagram: string;
}

export interface ContactInfo {
  secretariatAddress: string;
  emails: string[];
  website: string;
  socials: SocialHandles;
}

export interface LetterContent {
  id: string;
  category?: 'letter' | 'speech' | 'report' | 'bulletin' | 'agenda' | 'commendation';
  categoryLabel?: string;
  title: string;
  description?: string;
  date: string;
  refNo: string;
  recipient: string;
  subject: string;
  salutation: string;
  bodyParagraphs: string[];
  signatoryName: string;
  signatoryTitle: string;
  phone: string;
  email: string;
}

export interface LetterheadSettings {
  docType: DocType;
  templateId: TemplateId;
  themeId: ColorThemeId;
  fontPreset: FontPreset;
  customFontFamily?: 'cinzel' | 'playfair' | 'jakarta' | 'merriweather' | 'cormorant' | 'great-vibes';
  letterSpacing?: 'normal' | 'wide' | 'widest';
  logoSize: 'sm' | 'md' | 'lg' | 'xl';
  logoPlacement?: 'left' | 'center' | 'right' | 'dual';
  customLogoUrl?: string | null;
  secondaryLogoUrl?: string | null;
  customWatermarkUrl?: string | null;
  customHeaderBannerUrl?: string | null;
  headerBannerHeightPx?: number;
  watermarkOpacity: number;
  showWatermark: boolean;
  showHeaderBorder: boolean;
  showFooterBorder: boolean;
  signatureType: SignatureType;
  signatureText: string;
  signatureDrawData: string | null; // dataURL for drawn signature
  scannedSignatureUrl: string | null; // dataURL or uploaded URL for scanned signature image
  // Document Formatting & Page Layout Options
  fontSizePt?: number; // 10, 11, 12, 14, 16
  lineHeight?: 'tight' | 'normal' | 'relaxed' | 'double';
  paragraphSpacing?: 'compact' | 'normal' | 'spacious';
  documentPageMode?: 'single' | 'multi';
  showPageNumbers?: boolean;
  customMarginMm?: number;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  paperSize?: 'a4' | 'letter' | 'legal' | 'a3' | 'a5' | 'card';
  paperOrientation?: 'portrait' | 'landscape';
  royalStyleCategory?: RoyalStyleCategory;
  pageNumberStyle?: PageNumberStyle;
  borderStyle?: BorderStyleId;
  festivalStartDate?: string;
  showFestivalCountdown?: boolean;
  // Custom Page Margin Setup
  marginPreset?: MarginPreset;
  marginTopMm?: number;
  marginBottomMm?: number;
  marginLeftMm?: number;
  marginRightMm?: number;
  // Subsequent Page Margin Overrides (Page 2+)
  enableSubsequentMarginOverride?: boolean;
  subsequentMarginTopMm?: number;
  subsequentMarginBottomMm?: number;
  subsequentMarginLeftMm?: number;
  subsequentMarginRightMm?: number;
  // QR Code Overlay Settings
  showQrCode?: boolean;
  qrCodeTarget?: 'registration' | 'program' | 'verify' | 'custom';
  qrCodeCustomUrl?: string;
  qrCodePosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'header' | 'footer';
  qrCodeSizePx?: number;
  qrCodeLabel?: string;
  // Custom Page Footer Settings
  footerAddress?: string;
  footerEmail?: string;
  footerPhone?: string;
  footerWebsite?: string;
  footerMotto?: string;
  footerDisclaimer?: string;
  footerShowAddress?: boolean;
  footerShowContact?: boolean;
  footerShowSocials?: boolean;
  footerShowMotto?: boolean;
  footerAlignment?: 'center' | 'left' | 'between';
}

/* --- INVITATION CARD INTERFACE & PRESETS --- */
export interface InvitationContent {
  guestCategory: string; // e.g., 'SPECIAL GUEST OF HONOR', 'ROYAL DELEGATE', 'VIP'
  guestName: string;
  eventTitle: string;
  subTitle: string;
  dateStr: string;
  timeStr: string;
  venueName: string;
  venueAddress: string;
  rsvpName: string;
  rsvpPhone: string;
  dressCode: string;
  accessCode: string;
  invitationNote: string;
  signatoryTitle: string;
  phone: string;
  email: string;
}

/* --- COMPLIMENTARY / BUSINESS CARD INTERFACE & PRESETS --- */
export interface ComplimentaryCardContent {
  memberName: string;
  royalTitle: string;
  committeeRole: string;
  organization: string;
  phone: string;
  email: string;
  website: string;
  officeLocation: string;
  motto: string;
}

/* --- FESTIVAL POSTER / FLYER INTERFACE & PRESETS --- */
export interface PosterContent {
  mainTitle: string;
  subtitle: string;
  themeTagline: string;
  eventDates: string;
  venue: string;
  highlights: string[];
  royalPatron: string;
  specialGuestHeadline: string;
  entryInfo: string;
  contactPhones: string;
  email: string;
  websiteUrl: string;
}

/* --- CERTIFICATE OF HONOR & AWARD INTERFACE --- */
export interface CertificateContent {
  certificateNo: string;
  headerTitle: string;
  subTitle: string;
  presentedToPrefix: string;
  recipientName: string;
  awardTitle: string;
  citation: string;
  conferredDate: string;
  grandPatronName: string;
  grandPatronTitle: string;
  committeeChairName: string;
  committeeChairTitle: string;
}

/* --- EXECUTIVE MEMORANDUM INTERFACE --- */
export interface MemoContent {
  memoRefNo: string;
  dateStr: string;
  toRecipient: string;
  fromSender: string;
  classification: string; // e.g., 'HIGHLY CONFIDENTIAL', 'ROYAL DIRECTIVE', 'INTERNAL URGENT'
  subject: string;
  openingSummary: string;
  bodyParagraphs: string[];
  actionItems: string[];
  signatoryName: string;
  signatoryTitle: string;
}

/* --- ROYAL CEREMONY EVENT PROGRAM INTERFACE --- */
export interface ProgramContent {
  eventTitle: string;
  subTitle: string;
  dateAndVenue: string;
  theme: string;
  orderOfService: { time: string; item: string; lead: string }[];
  specialGuests: string[];
  royalPatrons: string[];
  closingBenediction: string;
}

/* --- ROYAL GAZETTE PROCLAMATION INTERFACE --- */
export interface ProclamationContent {
  proclamationNo: string;
  title: string;
  headerSubtitle: string;
  enactmentDate: string;
  preamble: string;
  clauses: string[];
  signManualName: string;
  signManualTitle: string;
  sealNote: string;
}

// Default Color Themes
export const COLOR_THEMES: ColorTheme[] = [
  {
    id: 'forest-gold',
    name: 'Uzairue Green & Gold',
    primary: 'text-[#0F5132]',
    primaryHex: '#0F5132',
    secondary: 'text-[#D4AF37]',
    secondaryHex: '#D4AF37',
    accent: 'bg-[#D4AF37]',
    accentHex: '#D4AF37',
    lightBg: 'bg-[#F4FAF6]'
  },
  {
    id: 'royal-blue',
    name: 'Royal Blue & Gold',
    primary: 'text-[#1D3557]',
    primaryHex: '#1D3557',
    secondary: 'text-[#C5A059]',
    secondaryHex: '#C5A059',
    accent: 'bg-[#C5A059]',
    accentHex: '#C5A059',
    lightBg: 'bg-[#F0F4F8]'
  },
  {
    id: 'imperial-burgundy',
    name: 'Imperial Burgundy',
    primary: 'text-[#722F37]',
    primaryHex: '#722F37',
    secondary: 'text-[#E5A93B]',
    secondaryHex: '#E5A93B',
    accent: 'bg-[#E5A93B]',
    accentHex: '#E5A93B',
    lightBg: 'bg-[#FAF5F6]'
  },
  {
    id: 'charcoal',
    name: 'Classic Charcoal',
    primary: 'text-[#2B2D42]',
    primaryHex: '#2B2D42',
    secondary: 'text-[#8D99AE]',
    secondaryHex: '#8D99AE',
    accent: 'bg-[#8D99AE]',
    accentHex: '#8D99AE',
    lightBg: 'bg-[#F8F9FA]'
  },
  {
    id: 'monarch-purple',
    name: 'Monarch Purple & Gold',
    primary: 'text-[#4A154B]',
    primaryHex: '#4A154B',
    secondary: 'text-[#F2C94C]',
    secondaryHex: '#F2C94C',
    accent: 'bg-[#F2C94C]',
    accentHex: '#F2C94C',
    lightBg: 'bg-[#FAF5FC]'
  },
  {
    id: 'emerald-jade',
    name: 'Emerald Jade & Platinum',
    primary: 'text-[#064E3B]',
    primaryHex: '#064E3B',
    secondary: 'text-[#34D399]',
    secondaryHex: '#34D399',
    accent: 'bg-[#34D399]',
    accentHex: '#34D399',
    lightBg: 'bg-[#ECFDF5]'
  }
];

export const DEFAULT_CONTACT_INFO: ContactInfo = {
  secretariatAddress: 'Palace of the Ogieneni of Uzairue Kingdom, Uzairue, Edo State, Nigeria',
  emails: ['erueuzairue@gmail.com', 'support@erueuzairue.org'],
  website: 'erueuzairue.org',
  socials: {
    facebook: '@erueuzairue',
    youtube: '@erueuzairue',
    twitter: '@erueuzairue',
    tiktok: '@erueuzairue',
    instagram: '@erueuzairue'
  }
};

export const SAMPLE_LETTERS: LetterContent[] = [
  {
    id: 'invitation',
    category: 'letter',
    categoryLabel: 'Invitation',
    title: 'Royal Festival Invitation',
    description: 'Formal VIP invitation extended to state governors, dignitaries and royal guests.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/INV-042',
    recipient: 'His Excellency,\nThe Executive Governor of Edo State,\nGovernment House, Benin City,\nEdo State, Nigeria.',
    subject: 'SPECIAL INVITATION TO THE ANNUAL ERUE FESTIVAL OF UZAIRUE KINGDOM 2026',
    salutation: 'Your Excellency,',
    bodyParagraphs: [
      'It is with great honor, on behalf of the Ogieneni of Uzairue Kingdom, the Uzairue Royal Council, and the entire people of Uzairue, that we formally extend this invitation to you as our Special Guest of Honor at the upcoming Annual Erue Festival of Uzairue Kingdom.',
      'The Erue Festival is our grandest annual celebration of who we are as a people—showcasing our rich cultural heritage, traditions, and values to the global community, while strengthening the bonds of unity across our kingdom for generations yet unborn. Under our shared motto—"One People, One Kingdom, One Destiny"—this year\'s festival promises a magnificent display of culture, music, food, and historical pride.',
      'We would be deeply humbled by your esteemed presence at the grand opening ceremony, scheduled to take place at the Royal Palace grounds. Your attendance will greatly inspire our youth and signify a shared commitment to preserving our rich cultural roots.',
      'A detailed festival program brochure is attached to this letter for your kind perusal. We look forward to receiving your favorable confirmation.'
    ],
    signatoryName: 'Prince Albert Uzairue',
    signatoryTitle: 'Chairman, Erue Festival Planning Committee',
    phone: '+234 (0) 803 123 4567',
    email: 'erueuzairue@gmail.com',
  },
  {
    id: 'sponsorship',
    category: 'letter',
    categoryLabel: 'Proposal',
    title: 'Sponsorship Proposal Request',
    description: 'Corporate partnership request outlining brand exposure and sponsorship tiers.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/SPON-109',
    recipient: 'The Managing Director,\nCorporate Partners & Brand Sponsors,\nCorporate Headquarters,\nLagos, Nigeria.',
    subject: 'OFFICIAL PARTNERSHIP AND SPONSORSHIP ENQUIRY: ANNUAL ERUE FESTIVAL OF UZAIRUE KINGDOM',
    salutation: 'Dear Sir / Madam,',
    bodyParagraphs: [
      'The Planning Committee of the Annual Erue Festival of Uzairue Kingdom is pleased to present this exclusive partnership proposal for the upcoming festival season. As a leading brand, we believe your organization aligns perfectly with the cultural prestige, massive public gathering, and community impact that the Erue Festival commands.',
      'The Erue Festival brings together over 100,000 physical attendees, including royal dignitaries, local indigenes, tourists, and cultural enthusiasts, alongside millions of digital viewers across our social media platforms (@erueuzairue). This presents an unparalleled opportunity for your company to drive brand engagement, product activation, and corporate social responsibility.',
      'We offer various sponsorship tiers (Platinum, Gold, Silver) with extensive benefits, including premium logo placement on the main stage, custom exhibition booths, product exclusivity, and mentions in all pre-event and live media broadcasts.',
      'We would appreciate the opportunity to discuss this proposal in detail and customize a package that meets your marketing objectives. Our team will follow up next week to schedule a brief call.'
    ],
    signatoryName: 'Chief Dr. Julius Eramah',
    signatoryTitle: 'Director of Corporate Sponsorship & Partnership',
    phone: '+234 (0) 802 987 6543',
    email: 'sponsorship@erueuzairue.org',
  },
  {
    id: 'royal-address',
    category: 'speech',
    categoryLabel: 'Royal Speech',
    title: 'Royal Opening Keynote Speech by HRH Ogieneni',
    description: 'Dignified royal address delivered by His Royal Highness celebrating peace and unity.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/SPCH-001',
    recipient: 'The Royal Council, Chiefs, Guests & People of Uzairue Kingdom,\nRoyal Palace Arena,\nUzairue Kingdom, Edo State.',
    subject: 'ROYAL OPENING ADDRESS DELIVERED BY HIS ROYAL HIGHNESS, THE OGIENENI OF UZAIRUE KINGDOM',
    salutation: 'My Beloved People of Uzairue, Distinguished Guests, and Friends of the Kingdom,',
    bodyParagraphs: [
      'It is with immense gratitude to Almighty God and the cherished spirits of our revered ancestors that I welcome you all to the Royal Palace Arena for the official opening of the Annual Erue Festival of Uzairue Kingdom. Today, we gather not merely to celebrate, but to reaffirm our unbreakable bond as one people with one common heritage.',
      'Our festival theme—"Uzairue Heritage: Culture, Security, Prosperity"—is the cornerstone of our peaceful coexistence. Through centuries of history, the Uzairue people have stood resilient, bound by love, hospitality, industriousness, and respect for our noble customs. The Erue Festival serves as our sacred sanctuary where generations meet, stories are retold, and young minds are inspired to uphold truth and honor.',
      'To our youth, I charge you to embrace education, peaceful innovation, and respect for our traditional values. Let the songs, dances, and ancient arts presented throughout this festival remind you of the rich blood of nobility that runs in your veins.',
      'I extend our warm royal hospitality to all visiting state governors, traditional rulers, corporate partners, and international tourists. May your stay in Uzairue Kingdom bring you joy, inspiration, and lasting memories. I hereby declare the Erue Festival officially open!'
    ],
    signatoryName: 'His Royal Highness, The Ogieneni of Uzairue',
    signatoryTitle: 'Paramount Ruler & Custodian of Uzairue Culture',
    phone: '+234 (0) 803 123 4567',
    email: 'palace@erueuzairue.org',
  },
  {
    id: 'keynote-speech',
    category: 'speech',
    categoryLabel: 'Keynote Speech',
    title: 'Festival Welcome Keynote Address',
    description: 'Official opening welcome address by the Planning Committee Chairman.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/SPCH-002',
    recipient: 'Your Excellency, Royal Fathers, Members of the Press & Delegates,\nUzairue Cultural Complex,\nEdo State, Nigeria.',
    subject: 'WELCOME ADDRESS DELIVERED AT THE GRAND OPENING CEREMONY OF THE ERUE FESTIVAL 2026',
    salutation: 'Your Excellency, Your Royal Highnesses, Highly Respected Chiefs, and Guests,',
    bodyParagraphs: [
      'On behalf of the Central Planning Committee, it gives me profound joy to welcome you all to the premier cultural spectacle of Edo State—the Annual Erue Festival of Uzairue Kingdom. Months of intense preparation and communal dedication have culminated in this glorious morning.',
      'This edition is uniquely crafted to highlight our indigenous arts, agricultural achievements, and youth empowerment initiatives. We have lined up over 20 traditional dance troupes, historical exhibitions, food carnivals, and an intellectual symposium on cultural preservation.',
      'We express our deepest homage to His Royal Highness, the Ogieneni of Uzairue, whose wise guidance and royal blessings have steered our committee toward achieving international standards of event execution.',
      'We encourage all attendees to participate vibrant in all scheduled sessions and experience the warmth of Uzairue hospitality. Thank you for honoring our kingdom with your presence.'
    ],
    signatoryName: 'Prince Albert Uzairue',
    signatoryTitle: 'Chairman, Erue Festival Central Directorate',
    phone: '+234 (0) 803 123 4567',
    email: 'chairman@erueuzairue.org',
  },
  {
    id: 'goodwill-message',
    category: 'speech',
    categoryLabel: 'Goodwill Message',
    title: 'Royal Goodwill Message to Uzairue Diaspora',
    description: 'Inspiring fraternal message sent to indigenes in North America, Europe, and Asia.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/GW-005',
    recipient: 'The President & Executives,\nUzairue Diaspora Union Worldwide (USA, UK, Canada & Europe Chapters),\nInternational Secretariat.',
    subject: 'ROYAL GOODWILL MESSAGE ON THE OCCASION OF THE ANNUAL ERUE FESTIVAL 2026',
    salutation: 'Dear Sons and Daughters of Uzairue in Diaspora,',
    bodyParagraphs: [
      'As we celebrate the Annual Erue Festival, the Ogieneni-in-Council sends warm royal greetings and heartfelt blessings to all indigenes residing across the globe. Distance can never sever the unbreakable umbilical cord that connects every child of Uzairue to our ancestral homeland.',
      'We commend your tireless efforts in projecting the good name of Uzairue internationally, supporting community development projects back home, and mentoring the younger generation in diaspora to treasure their roots.',
      'Though many of you are physically distant during this festival season, your spirit is felt in every drumbeat, chant, and royal blessing. We encourage you to tune into our official live broadcasts on erueuzairue.org and @erueuzairue social channels.',
      'May the spirit of unity and prosperity abide with you and your families. We look forward to welcoming you home for our upcoming homecoming civic reception.'
    ],
    signatoryName: 'High Chief Dr. Osasere Eramah',
    signatoryTitle: 'Palace Envoy for Diaspora Affairs & Cultural Diplomacy',
    phone: '+234 (0) 802 987 6543',
    email: 'diaspora@erueuzairue.org',
  },
  {
    id: 'annual-report',
    category: 'report',
    categoryLabel: 'Executive Report',
    title: 'Annual Kingdom Heritage & Infrastructure Report',
    description: 'Comprehensive report on festival readiness, community development projects and archives.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/REP-001',
    recipient: 'The Ogieneni-in-Council & General Assembly of Uzairue Indigenes,\nPalace Secretariat,\nUzairue Kingdom, Edo State.',
    subject: 'ANNUAL PROGRESS & HERITAGE PRESERVATION REPORT FOR UZAIRUE KINGDOM (2025/2026)',
    salutation: 'Your Royal Highness and Respected Chiefs in Council,',
    bodyParagraphs: [
      'EXECUTIVE SUMMARY: This annual report presents a detailed account of kingdom affairs, cultural archive digitization, community infrastructure milestones, and festival preparations accomplished during the 2025/2026 operational year.',
      '1. CULTURAL PRESERVATION & DIGITIZATION: Over 150 historical artifacts, oral tradition audio recordings, and royal lineage documents have been cataloged into the newly launched digital portal at erueuzairue.org to educate researchers globally.',
      '2. COMMUNITY WATER & INFRASTRUCTURE: In partnership with corporate sponsors, three solar-powered boreholes and modern solar streetlighting were successfully installed along the main Royal Palace Avenue to ensure safety during night festival parades.',
      '3. YOUTH SCHOLARSHIP FUND: Through donor generous contributions, 45 university undergraduates from Uzairue Kingdom received educational grants totaling ₦15 Million during the academic session under review.',
      '4. RECOMMENDATIONS FOR 2027: We recommend expanding the festival arena capacity by 30% and instituting an annual Uzairue Innovation Fair to attract tech hubs and eco-tourism investors to the kingdom.'
    ],
    signatoryName: 'Engr. Fidelis Aigbe',
    signatoryTitle: 'Head of Secretariat & Chief Technical Officer',
    phone: '+234 (0) 803 999 0112',
    email: 'reports@erueuzairue.org',
  },
  {
    id: 'security-report',
    category: 'report',
    categoryLabel: 'Protocol Briefing',
    title: 'Security, Traffic & Medical Readiness Briefing',
    description: 'Detailed briefing on security deployment, VIP protocols, traffic control, and emergency services.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/SEC-088',
    recipient: 'The Chairman & Executive Members,\nErue Festival Central Security Directorate,\nUzairue Kingdom.',
    subject: 'COMPREHENSIVE SECURITY, HEALTH & TRAFFIC PROTOCOL REPORT FOR ERUE FESTIVAL 2026',
    salutation: 'Distinguished Committee Members,',
    bodyParagraphs: [
      'LOGISTICS & SAFETY OVERVIEW: The Security Sub-Committee, in joint operations with the Edo State Police Command, Federal Road Safety Corps (FRSC), and local vigilante corps, has finalized security positioning across all event zones.',
      'PERSONNEL DEPLOYMENT: A total of 250 trained security personnel and 80 ceremonial palace guards will be deployed to manage crowd flow, escort VIP motorcades, and secure perimeter access gates during all major sessions.',
      'TRAFFIC & PARKING ZONES: Traffic along the Auchi-Uzairue Expressway will be diverted between 8:00 AM and 6:00 PM on peak festival days. Designated VIP parking is located at Gate 1, while general public buses will utilize Gate 3.',
      'MEDICAL EMERGENCY POSTS: Two fully equipped mobile ambulances and a 10-bed emergency medical tent staffed by red cross volunteers will operate 24/7 at the Palace Arena.'
    ],
    signatoryName: 'Col. Francis Braimah (Rtd.)',
    signatoryTitle: 'Chief Security Officer & Protocol Director',
    phone: '+234 (0) 805 111 2233',
    email: 'security@erueuzairue.org',
  },
  {
    id: 'financial-report',
    category: 'report',
    categoryLabel: 'Financial Report',
    title: 'Sponsorship Revenue & Expenditure Audit',
    description: 'Audited financial summary covering partner contributions, stage rentals, and logistics cost.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/FIN-204',
    recipient: 'The Board of Trustees & Corporate Donors,\nUzairue Festival Endowment Board,\nEdo State, Nigeria.',
    subject: 'ANNUAL ERUE FESTIVAL AUDITED SPONSORSHIP & DONATION SUMMARY REPORT',
    salutation: 'Respected Trustees and Corporate Partners,',
    bodyParagraphs: [
      'FINANCIAL STATEMENT: We present the interim audited financial statement covering corporate sponsorships, state government grants, private philanthropic donations, and operational expenditures for the 2026 festival cycle.',
      'REVENUE SUMMARY: Total realized revenue stands at ₦48.5 Million, comprising ₦28M from corporate partners, ₦12M from diaspora contributions, and ₦8.5M from exhibition booth rentals.',
      'EXPENDITURE BREAKDOWN: Key disbursements include Stage & Multimedia Engineering (35%), Cultural Troupe Honoraria (25%), Security & Public Address Systems (20%), and Media Broadcasting (20%).',
      'GOVERNANCE & AUDIT ASSURANCE: All financial transactions passed rigorous internal audit verification. Complete bank statements and receipt vouchers are available for inspection at the Secretariat.'
    ],
    signatoryName: 'Mrs. Patience Oshiomhole, FCA',
    signatoryTitle: 'Chief Financial Auditor & Treasurer',
    phone: '+234 (0) 803 777 8899',
    email: 'audit@erueuzairue.org',
  },
  {
    id: 'press-release',
    category: 'bulletin',
    categoryLabel: 'Press Release',
    title: 'Official Press Release: Festival Kickoff',
    description: 'Formal announcement to news outlets, broadcasters and digital media platforms.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/PR-001',
    recipient: 'To All Media Houses,\nCultural Correspondents & Press Editors,\nNigeria & Worldwide.',
    subject: 'PRESS RELEASE: PREPARATIONS KICK OFF FOR THE ANNUAL ERUE FESTIVAL OF UZAIRUE KINGDOM',
    salutation: 'FOR IMMEDIATE RELEASE',
    bodyParagraphs: [
      'The Palace of the Ogieneni of Uzairue Kingdom has officially announced the commencement of preparatory activities for the highly anticipated Annual Erue Festival of Uzairue Kingdom. Known as the cultural heartbeat of the kingdom, the festival brings together all clans of Uzairue to celebrate cultural display, traditional music & dance, and the rich culinary heritage that defines us.',
      'This year\'s theme, "Uzairue Heritage: Culture, Security, Prosperity," centers on heritage, security, and community prosperity. It is an open invitation to cultural enthusiasts, tourists, and neighboring communities to join us in celebrating our rich ancestral roots and promoting peace.',
      'Special highlights for the upcoming edition include: the Royal Dance of the Maidens, the Coronation Exhibition, historical storytelling sessions, and the Uzairue Unity Concert featuring prominent indigenous artists.',
      'For sponsorship inquiries, media accreditation, and live-stream updates, please visit our website at erueuzairue.org or follow our verified social handles @erueuzairue.'
    ],
    signatoryName: 'High Chief Alhaji Musa Ibrahim',
    signatoryTitle: 'Palace Press Secretary & Publicity Director',
    phone: '+234 (0) 803 555 0199',
    email: 'press@erueuzairue.org',
  },
  {
    id: 'royal-bulletin',
    category: 'bulletin',
    categoryLabel: 'Palace Gazette',
    title: 'Palace Gazette & Community Road Advisory',
    description: 'Public notice on festival road closures, royal procession routes, and visitor guidelines.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/BUL-012',
    recipient: 'Notice to All Residents, Visitors, Commercial Operators & Chieftaincy Troupes,\nUzairue Kingdom.',
    subject: 'PALACE BULLETIN NO. 04: COMMUNITY GUIDELINES, DRESS CODE & ROAD CLOSURE NOTICE',
    salutation: 'GENERAL PUBLIC ADVISORY NOTICE',
    bodyParagraphs: [
      'By direction of the Ogieneni-in-Council, all residents and commercial transport operators are hereby notified of temporary road management measures during the peak days of the Annual Erue Festival.',
      'DRESS CODE & DECORUM: Visitors attending royal palace sessions are requested to dress respectfully in traditional African attire or formal modesty clothes in honor of our sacred cultural traditions.',
      'NOISE REGULATION & TRADITIONAL FLUTES: Commercial public address systems outside designated festival arenas are restricted past 11:00 PM to ensure peace across residential quarters.',
      'EMERGENCY HELPLINES: For medical assistance, lost property or security queries, contact the Secretariat hotline at +234 803 123 4567 or visit the Information Kiosk at Gate 2.'
    ],
    signatoryName: 'Palace Information Directorate',
    signatoryTitle: 'Office of the Royal Secretariat, Uzairue Palace',
    phone: '+234 (0) 803 123 4567',
    email: 'bulletin@erueuzairue.org',
  },
  {
    id: 'media-bulletin',
    category: 'bulletin',
    categoryLabel: 'Media Bulletin',
    title: 'Media Broadcasting & Live Stream Directive',
    description: 'Guidelines for television crews, radio broadcasters, drone operators and digital creators.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/PR-009',
    recipient: 'To All Accredited Television Crews, Photojournalists & Digital Content Creators,\nMedia Desk.',
    subject: 'OFFICIAL BULLETIN: BROADCASTING RIGHTS, DRONE FILMING & SOCIAL MEDIA GUIDELINES',
    salutation: 'Attention Accredited Media Representatives,',
    bodyParagraphs: [
      'MEDIA CREW REGISTRATION: All journalists, camera crews, and digital content creators seeking access to the press gallery must collect their color-coded accreditation badges from the Secretariat Desk.',
      'DRONE FILMING PERMITS: Drone aerial photography inside the Royal Palace Arena is strictly restricted to operators holding written clearance from the Palace Media Directorate for safety reasons.',
      'LIVE STREAMING FEEDS: High-definition satellite feed parameters and official YouTube embed codes will be distributed 2 hours prior to the royal procession for syndication.',
      'INTERVIEW ZONE PROTOCOL: Royal chiefs and guest dignitaries may be interviewed exclusively within the designated Media Lounge located at the West Wing.'
    ],
    signatoryName: 'High Chief Alhaji Musa Ibrahim',
    signatoryTitle: 'Palace Press Secretary & Publicity Director',
    phone: '+234 (0) 803 555 0199',
    email: 'media@erueuzairue.org',
  },
  {
    id: 'event-agenda',
    category: 'agenda',
    categoryLabel: 'Event Agenda',
    title: 'Grand Finale Master Agenda & Timeline',
    description: 'Hour-by-hour operational timeline for main festival day, royal dance, and awards ceremony.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/AGEN-101',
    recipient: 'To All Protocol Officers, Master of Ceremonies & Ceremonial Ushers,\nEvent Command Center.',
    subject: 'OFFICIAL MASTER AGENDA & HOUR-BY-HOUR TIMELINE FOR ERUE FESTIVAL GRAND FINALE',
    salutation: 'To All Event Coordinators and Protocol Leads,',
    bodyParagraphs: [
      '09:00 AM – 10:00 AM: Arrival and seating of general guests, cultural troupes, and accredited press corps at the Palace Arena.',
      '10:00 AM – 10:30 AM: VIP Procession & Arrival of Executive Governors, Royal Fathers, and Foreign Diplomats.',
      '10:30 AM – 11:30 AM: Grand Royal Entry of HRH The Ogieneni of Uzairue accompanied by Royal Trumpeters and Council of Chiefs.',
      '11:30 AM – 01:00 PM: Royal Opening Keynote Address, Maiden Cultural Dance Display, and Traditional Warrior Exhibition.',
      '01:00 PM – 03:00 PM: Conferment of Royal Erue Heritage Honors, Civic Banquet Reception, and Grand Unity Concert.'
    ],
    signatoryName: 'Protocol Directorate',
    signatoryTitle: 'Uzairue Central Ceremonial Planning Committee',
    phone: '+234 (0) 803 123 4567',
    email: 'agenda@erueuzairue.org',
  },
  {
    id: 'symposium-program',
    category: 'agenda',
    categoryLabel: 'Symposium Agenda',
    title: 'Youth Empowerment & Cultural Symposium Agenda',
    description: 'Schedule for academic panels, art exhibitions, and youth leadership workshops.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/PROG-055',
    recipient: 'To All Delegates, Panelists, Student Leaders & Academic Observers,\nUzairue Town Hall.',
    subject: 'PROGRAMME OF EVENTS: UZAIRUE CULTURAL HERITAGE & YOUTH INNOVATION SYMPOSIUM',
    salutation: 'Dear Distinguished Delegates,',
    bodyParagraphs: [
      'SESSION 1 (09:30 AM): Opening Remarks on "Preserving African Heritage in the Digital Age" by Keynote Speaker Prof. E. Uzairue.',
      'SESSION 2 (11:00 AM): Interactive Panel Discussion featuring youth tech entrepreneurs and traditional craftsmen.',
      'SESSION 3 (01:30 PM): Exhibition of indigenous agricultural products, weaving arts, and historical bronze sculptures.',
      'SESSION 4 (03:00 PM): Announcement of the Uzairue Youth Innovation Grants & Certificate Presentation.'
    ],
    signatoryName: 'Dr. (Mrs.) Angela Eramah',
    signatoryTitle: 'Symposium Convener & Director of Education',
    phone: '+234 (0) 802 333 4455',
    email: 'symposium@erueuzairue.org',
  },
  {
    id: 'commendation-letter',
    category: 'commendation',
    categoryLabel: 'Royal Commendation',
    title: 'Royal Letter of Commendation & Honor Nomination',
    description: 'Official recognition letter conferring honor and praising philanthropic contributions.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/COM-077',
    recipient: 'High Chief Dr. Osasere Eramah,\nBenin City, Edo State,\nNigeria.',
    subject: 'ROYAL LETTER OF COMMENDATION FOR EXCEPTIONAL PHILANTHROPY AND COMMUNITY SERVICE',
    salutation: 'Respected High Chief,',
    bodyParagraphs: [
      'I am directed by His Royal Highness, the Ogieneni of Uzairue Kingdom, to convey to you this Royal Letter of Commendation in profound recognition of your steadfast commitment to the welfare and progress of Uzairue Kingdom.',
      'Your philanthropic interventions—most notably the renovation of primary healthcare clinics and the sponsorship of rural youth education—have touched countlessly many lives and brought immense honor to our kingdom.',
      'In tribute to your exemplary leadership, the Uzairue Royal Traditional Council has nominated you for the prestigious "Grand Order of the Royal Erue Heritage Award" to be presented at the upcoming festival banquet.',
      'We extend our heartfelt congratulations and pray that your life continues to be blessed with health, wisdom, and distinguished service to humanity.'
    ],
    signatoryName: 'Prince Albert Uzairue',
    signatoryTitle: 'Chairman, Royal Honors & Awards Council',
    phone: '+234 (0) 803 123 4567',
    email: 'honors@erueuzairue.org',
  },
  {
    id: 'diplomatic-letter',
    category: 'letter',
    categoryLabel: 'Diplomatic Letter',
    title: 'Fraternal Message to Sister Royal Majesties',
    description: 'Formal diplomatic letter sent to neighboring paramount rulers and traditional councils.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/DIP-033',
    recipient: 'His Royal Majesty,\nThe Oba / Paramount Ruler of Neighboring Kingdom,\nRoyal Palace,\nEdo State, Nigeria.',
    subject: 'FRATERNAL GREETINGS AND INVITATION TO THE ANNUAL ERUE FESTIVAL OF UZAIRUE KINGDOM',
    salutation: 'Your Royal Majesty, My Respected Royal Brother,',
    bodyParagraphs: [
      'On behalf of the Ogieneni of Uzairue Kingdom, the Royal Traditional Council, and the entire populace of Uzairue, I send fraternal greetings of goodwill, peace, and royal solidarity to Your Royal Majesty and your revered kingdom.',
      'The bond of friendship and historic cultural ties between our two kingdoms have long served as a beacon of unity and peaceful co-existence across our region. It is in this spirit of brotherly affection that we extend a formal royal invitation to Your Royal Majesty to grace our Annual Erue Festival as our Royal Guest.',
      'Your presence alongside your Council of Chiefs will greatly enrich our celebration and further solidify the harmonious relations between our thrones.',
      'We eagerly await the pleasure of welcoming your royal delegation to the Ogieneni Palace Arena. Please accept the assurances of our highest esteem and brotherly consideration.'
    ],
    signatoryName: 'His Royal Highness, The Ogieneni of Uzairue',
    signatoryTitle: 'Paramount Ruler of Uzairue Kingdom',
    phone: '+234 (0) 803 123 4567',
    email: 'diplomatic@erueuzairue.org',
  },
  {
    id: 'blank',
    category: 'letter',
    categoryLabel: 'Blank Starter',
    title: 'Blank Formal Letter Starter',
    description: 'Clean structured formal letter template ready for custom typing.',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    refNo: 'ERUE/2026/LTR-001',
    recipient: 'Recipient Name / Title\nOrganization / Office\nCity, State, Country',
    subject: 'RE: SUBJECT OF THE OFFICIAL CORRESPONDENCE',
    salutation: 'Dear Respected Sir / Madam,',
    bodyParagraphs: [
      'This is the first paragraph of your official letter. Click anywhere in the text editor in the sidebar or directly edit the fields to modify this content. The live preview will update immediately in high-resolution.',
      'You can add multiple paragraphs to structure your letter properly. Use the controls to adjust margins, template design layouts, typography options, and toggle the watermark.'
    ],
    signatoryName: 'Prince Albert Uzairue',
    signatoryTitle: 'Chairman, Erue Festival Planning Committee',
    phone: '+234 (0) 803 123 4567',
    email: 'erueuzairue@gmail.com',
  }
];

export const DEFAULT_INVITATION: InvitationContent = {
  guestCategory: 'SPECIAL GUEST OF HONOR',
  guestName: 'His Excellency, The Executive Governor',
  eventTitle: 'ANNUAL ERUE FESTIVAL OF UZAIRUE KINGDOM 2026',
  subTitle: 'A Royal Celebration of Heritage, Unity & Destiny',
  dateStr: 'Wednesday, 30th December 2026',
  timeStr: '10:00 AM Prompt (Red Carpet: 9:00 AM)',
  venueName: 'The Royal Palace Grounds',
  venueAddress: 'Ogieneni Palace Arena, Uzairue Kingdom, Edo State',
  rsvpName: 'Chief Planning Officer / Palace Secretariat',
  rsvpPhone: '+234 (0) 803 123 4567',
  dressCode: 'Royal Traditional / Cultural Attire',
  accessCode: 'ERUE-ROYAL-VIP-2026',
  invitationNote: 'Under the Royal Auspices of His Royal Highness, The Ogieneni of Uzairue Kingdom. Admittance is strictly by Invitation Card.',
  signatoryTitle: 'By Royal Command of the Uzairue Royal Council',
  phone: '+234 (0) 803 123 4567',
  email: 'invitations@erueuzairue.org',
};

export const DEFAULT_COMPLIMENTARY: ComplimentaryCardContent = {
  memberName: 'Prince Albert Uzairue',
  royalTitle: 'Executive Chairman',
  committeeRole: 'Erue Festival Central Planning Committee',
  organization: 'Palace of the Ogieneni of Uzairue Kingdom',
  phone: '+234 (0) 802 987 6543',
  email: 'albert@erueuzairue.org',
  website: 'erueuzairue.org',
  officeLocation: 'Royal Palace Complex, Uzairue, Edo State, Nigeria',
  motto: 'Uzairue Heritage: Culture, Security, Prosperity'
};

export const DEFAULT_POSTER: PosterContent = {
  mainTitle: 'ANNUAL ERUE FESTIVAL OF UZAIRUE KINGDOM',
  subtitle: 'UZAIRUE KINGDOM CULTURAL CELEBRATION 2026',
  themeTagline: 'UZAIRUE HERITAGE: CULTURE, SECURITY, PROSPERITY',
  eventDates: '25th – 31st OCTOBER 2026',
  venue: 'OGIENENI ROYAL PALACE ARENA, UZAIRUE, EDO STATE',
  highlights: [
    '👑 Grand Royal Ogiene Procession & Blessing',
    '💃 Royal Maidens & Warrior Cultural Dance',
    '🎺 Indigenous Music & Youth Unity Concert',
    '🍲 Uzairue Culinary & Minerals Heritage Exhibition',
    '🎨 Historical Storytelling & Coronation Arts'
  ],
  royalPatron: 'HOSTED BY HIS ROYAL HIGHNESS, THE OGIENENI OF UZAIRUE',
  specialGuestHeadline: 'Welcoming Dignitaries, Indigenes & Tourists Worldwide',
  entryInfo: 'FREE ADMISSION • ALL CULTURAL TROUPES WELCOME',
  contactPhones: '+234 (0) 803 123 4567 | +234 (0) 802 987 6543',
  email: 'info@erueuzairue.org',
  websiteUrl: 'erueuzairue.org | @erueuzairue'
};

export const DEFAULT_CERTIFICATE: CertificateContent = {
  certificateNo: 'UZAIRUE/HON/2026/088',
  headerTitle: 'ROYAL CERTIFICATE OF HONOR & DIGNITY',
  subTitle: 'PALACE OF THE OGIENENI OF UZAIRUE KINGDOM • EDO STATE, NIGERIA',
  presentedToPrefix: 'THIS IS TO CERTIFY THAT THE ROYAL COUNCIL HAS CONFERRED HONORS UPON',
  recipientName: 'High Chief Dr. Osasere Eramah',
  awardTitle: 'GRAND ORDER OF THE ROYAL ERUE HERITAGE AWARD',
  citation: 'In recognition of outstanding dedication to cultural preservation, exemplary philanthropy, community development, and steadfast promotion of unity across Uzairue Kingdom. Given under our Royal Seal & Sign Manual this day.',
  conferredDate: '30th December 2026',
  grandPatronName: 'HRH The Ogieneni of Uzairue',
  grandPatronTitle: 'Paramount Ruler & Custodian of Uzairue Tradition',
  committeeChairName: 'Prince Albert Uzairue',
  committeeChairTitle: 'Chairman, Royal Honors & Festival Council'
};

export const DEFAULT_MEMO: MemoContent = {
  memoRefNo: 'ERUE/2026/MEMO-019',
  dateStr: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
  toRecipient: 'All Committee Members, Clan Heads & Palace Chiefs',
  fromSender: 'Office of the Royal Secretariat & Festival Central Directorate',
  classification: 'ROYAL PALACE DIRECTIVE',
  subject: 'FINAL LOGISTICS & SECURITY PREPARATIONS FOR ERUE FESTIVAL 2026',
  openingSummary: 'Please be informed that the final harmonization meeting regarding protocol, cultural dance troupes, security deployment, and media coverage for the Erue Festival will hold at the Ogieneni Royal Palace Hall.',
  bodyParagraphs: [
    'All sub-committees are required to submit their finalized budget performance reports and list of accredited protocol officers to the Secretariat no later than 48 hours before the main event.',
    'Palace Chiefs and Clan Representatives are requested to verify their royal ceremonial robes and ensure all traditional troupes are fully briefed on performance timings to maintain decorum.'
  ],
  actionItems: [
    'Submit Sub-Committee Protocol List to Palace Secretariat',
    'Confirm VIP Security escorts with Edo State Police Command',
    'Finalize Live Stream Broadcast testing with Media Crew (@erueuzairue)'
  ],
  signatoryName: 'Prince Albert Uzairue',
  signatoryTitle: 'Executive Secretary, Uzairue Central Council'
};

export const DEFAULT_PROGRAM: ProgramContent = {
  eventTitle: 'GRAND ERUE FESTIVAL ROYAL BANQUET & OPENING CEREMONY',
  subTitle: 'ORDER OF EVENTS & ROYAL CEREMONIAL PROGRAM',
  dateAndVenue: 'Wednesday, 30th December 2026 • Royal Palace Arena, Uzairue',
  theme: 'Uzairue Heritage: Culture, Security, Prosperity',
  orderOfService: [
    { time: '09:00 AM', item: 'Red Carpet & Arrival of Royal Guests & Cultural Troupes', lead: 'Protocol Secretariat' },
    { time: '10:00 AM', item: 'Royal Entry of HRH The Ogieneni of Uzairue & Council of Chiefs', lead: 'Palace Trumpeters' },
    { time: '10:30 AM', item: 'National & Uzairue Royal Anthem / Opening Prayers', lead: 'High Chief Royal Priest' },
    { time: '11:00 AM', item: 'Welcome Address by Central Planning Committee Chairman', lead: 'Prince Albert Uzairue' },
    { time: '11:30 AM', item: 'Grand Royal Blessing & Opening Speech by HRH Ogieneni', lead: 'His Royal Highness' },
    { time: '12:30 PM', item: 'Royal Maiden Cultural Dance & Warrior Display', lead: 'Uzairue Troupe' },
    { time: '02:00 PM', item: 'Presentation of Royal Erue Heritage Awards & Honors', lead: 'Honors Committee' },
    { time: '03:30 PM', item: 'Vote of Thanks & Royal Banquet Reception', lead: 'Palace Press Secretary' }
  ],
  specialGuests: [
    'His Excellency, The Executive Governor of Edo State',
    'Royal Fathers & Paramount Rulers from Neighboring Kingdoms',
    'Captains of Industry & Corporate Partners'
  ],
  royalPatrons: [
    'His Royal Highness, The Ogieneni of Uzairue Kingdom',
    'The Uzairue Royal Traditional Council'
  ],
  closingBenediction: 'May the ancestors bless Uzairue Kingdom with peace, prosperity, and enduring unity.'
};

export const DEFAULT_PROCLAMATION: ProclamationContent = {
  proclamationNo: 'ROYAL GAZETTE NO. 04 OF 2026',
  title: 'BY THE COMMAND OF HIS ROYAL HIGHNESS THE OGIENENI OF UZAIRUE KINGDOM',
  headerSubtitle: 'A ROYAL PROCLAMATION REGARDING THE ERUE FESTIVAL & KINGDOM UNITY',
  enactmentDate: 'Given under our Royal Sign Manual at the Ogieneni Palace this 31st Day of October 2026',
  preamble: 'WHEREAS, it has been the ancient custom and honored tradition of Uzairue Kingdom to unite all indigenes, clans, and friends in peace, progress, and mutual goodwill through the celebration of the Annual Erue Festival;',
  clauses: [
    'BE IT KNOWN TO ALL MEN that the 25th through 31st days of October 2026 are hereby declared Official Royal Heritage Days throughout the length and breadth of Uzairue Kingdom.',
    'ALL CLANS AND TOWNS OF UZAIRUE are enjoined to observe the festival with dignity, unity, hospitality, and total adherence to the rich traditions of our forefathers.',
    'NO INDIVIDUAL OR GROUP shall engage in any act detrimental to the peace, serenity, and honor of Uzairue Kingdom during the period of the Royal Festival.'
  ],
  signManualName: 'His Royal Highness, The Ogieneni of Uzairue',
  signManualTitle: 'Paramount Ruler & Custodian of Uzairue Heritage',
  sealNote: 'SEALED WITH THE GREAT ROYAL WAX SEAL OF UZAIRUE KINGDOM'
};

// ==========================================
// ERUE FESTIVAL PLATFORM & RAFFLE SUITE TYPES
// ==========================================

export type PortalTab = 
  // Existing tabs (preserved)
  | 'festival-home'
  | 'heritage'
  | 'schedule'
  | 'calendar'
  | 'raffle'
  | 'store'
  | 'resources-investment'
  | 'communication-portal'
  | 'publishing-suite'
  | 'financial-manager'
  | 'reports'
  | 'visitor-guide'
  | 'news'
  | 'sponsorship'
  // NEW PUBLIC MODULES
  | 'diaspora-portal'          // 🌍 Diaspora hub
  | 'communities'              // 🏘️ 22 Villages showcase
  | 'community-detail'         // 🏛️ Individual community page
  | 'registrations'            // 📝 Event registrations hub
  | 'voting'                   // 🗳️ Miss ERUE voting
  | 'live-stream'              // 📺 Live streaming
  | 'gallery'                  // 📸 Photo/video gallery
  | 'contact'                  // 📞 Public contact form
  | 'programme'                // 📋 Detailed programme
  | 'ogieneni'                 // 👑 The Ogieneni page
  | 'cwc'                      // 🏛️ Central Working Committee
  | 'loc'
  | 'dignitaries' | 'donate' | 'vendors'
  | 'financial-hub'
  | 'contacts-directory'
  | "governance-admin"
  | "document-manager"
  | "accounting"
  | "budget"
  | "raffle-admin"
  | "community-admin"
  | "vendor-admin"
  | "publishing-suite"
  | "financial-manager"
  | "communication-portal"
  | "resources-investment"
  | "schedule"
  | "calendar"
  | "management-committee"
  | "board-of-trustees"
  | "fundraising"
  | "fundraising-admin"
  | "vendor-register"
  | "paystack-config"
  | "raffle-admin"
  | "uzairue-portal"          // 🏛️ Uzairue Kingdom Portal
  | 'collaboration'
  | 'meta-distribution'
; // DROP 33 — complete PortalTab union

// NEW: Additional community context type
export interface CommunityDetailContext {
  villageId: string;
  villageName: string;
}

export interface SavedDraft {
  id: string;
  name: string;
  docType: DocType;
  updatedAt: string;
  settings: LetterheadSettings;
  content: any;
  tag?: DocTag;
  workflowState?: DocumentWorkflowState;
}

export interface PaystackStoreItem {
  id: string;
  name: string;
  category: 'VIP Event Tickets' | 'Branded Apparel' | 'Royal Souvenirs' | 'Fundraising Gift Cards';
  price: number;
  imageEmoji: string;
  description: string;
  stockStatus: string;
  badge?: string;
}

export interface CartItem {
  item: PaystackStoreItem;
  quantity: number;
}

export interface PaystackTransactionRecord {
  reference: string;
  email: string;
  name: string;
  phone: string;
  amount: number;
  items: { name: string; qty: number; price: number }[];
  purchasedAt: string;
  status: 'success' | 'pending' | 'failed';
  paymentChannel: 'Paystack Card / Transfer / USSD';
}

export type RaffleTierId = 'mega' | 'midi' | 'mini' | 'starter';

export interface RafflePrizeItem {
  id: string;
  rank: number;
  title: string;
  prizeType: 'item' | 'cash' | 'consolation' | 'bonus_offer';
  description: string;
  cashAmountValue?: number;
  estimatedValueLabel: string;
  sponsorName: string;
  icon: string;
  imageUrl?: string;
  totalWinningSlots: number;
  drawnSlotsCount: number;
  winnerTicketNumbers?: string[];
}

export interface RaffleTierConfig {
  id: RaffleTierId;
  name: string;
  tagline: string;
  badgeEmoji: string;
  ticketPrice: number;
  ticketPriceUsd: number;
  drawDate: string;
  drawVenue: string;
  totalWinningSlotsCount: number;
  maxTicketsLimit?: number;
  status: 'open' | 'drawing' | 'drawn' | 'settled' | 'paused';
  bonusOfferNote?: string;
  bonusMiniTicketsPerTicket?: number;
  colorTheme: {
    bgGradient: string;
    border: string;
    accentBg: string;
    textAccent: string;
  };
  prizes: RafflePrizeItem[];
}

export interface RaffleTicket {
  id: string;
  ticketNumber: string; // e.g., MEGA-2026-8849
  tierId: RaffleTierId;
  tierName: string;
  buyerName: string;
  buyerPhone: string;
  buyerEmail: string;
  buyerClanOrCity: string;
  quantity: number;
  bonusTicketsCount?: number;
  bonusTicketNumbers?: string[];
  unitPrice: number;
  totalAmount: number;
  purchasedAt: string;
  status: 'active' | 'won' | 'claimed' | 'settled';
  winningPrizeTitle?: string;
  winningPrizeType?: 'item' | 'cash' | 'consolation' | 'bonus_offer';
  winningRank?: number;
  winningCashAmount?: number;
  verificationHash: string;
  secretVerificationPin: string;
  drawTimestamp?: string;
  drawnByOfficial?: string;
  settlementStatus?: 'unclaimed' | 'pending_verification' | 'approved_for_payout' | 'disbursed';
  settlementReferenceNo?: string;
  settledAt?: string;
  bankDetailsForPayout?: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  };
}

export interface WinnerSettlementRecord {
  id: string;
  ticketNumber: string;
  tierId: RaffleTierId;
  tierName: string;
  winnerName: string;
  winnerPhone: string;
  winnerEmail: string;
  winnerClanOrCity: string;
  prizeTitle: string;
  prizeType: 'item' | 'cash' | 'consolation' | 'bonus_offer';
  cashAmountValue?: number;
  verificationPin: string;
  verificationHash: string;
  status: 'unclaimed' | 'pending_verification' | 'approved_for_payout' | 'disbursed';
  verificationNotes?: string;
  payoutBankDetails?: {
    bankName: string;
    accountNumber: string;
    accountName: string;
  };
  payoutReceiptVoucherNo?: string;
  approvedByOfficial?: string;
  settledAt?: string;
  claimDate?: string;
}

export interface RafflePrize {
  id: string;
  rank: number;
  title: string;
  description: string;
  category: 'Grand Prize' | 'First Prize' | 'Second Prize' | 'Consolation Prize' | 'Special Heritage Award';
  sponsor: string;
  valueEst: string;
  icon: string;
  quantity: number;
  claimedCount: number;
  winnerTicketNumbers?: string[];
}

export interface FestivalEvent {
  id: string;
  dayNumber: number;
  dateStr: string;
  timeStr: string;
  title: string;
  venue: string;
  category: 'Ceremonial' | 'Cultural Dance' | 'Raffle Draw' | 'Sports & Wrestling' | 'Royal Banquet' | 'Youth Carnival';
  description: string;
  isHighlight?: boolean;
}

export interface NewsArticle {
  id: string;
  title: string;
  date: string;
  category: 'Royal Announcement' | 'Festival Update' | 'Raffle News' | 'Cultural Spotlight' | 'Diaspora Brief';
  summary: string;
  fullBody: string;
  author: string;
  imageEmoji: string;
  isFeatured?: boolean;
}

export interface SponsorPackage {
  id: string;
  tier: 'Royal Diamond' | 'Gold Heritage' | 'Silver Patron' | 'Bronze Supporter' | 'Friends of Uzairue';
  priceLabel: string;
  benefits: string[];
  colorTheme: string;
  icon: string;
}

// ============================================
// NEW MODULE INTERFACES (Sprint 1, Drop 1)
// ============================================

// ============================================
// DIASPORA PORTAL TYPES
// ============================================

export interface DiasporaProfile {
  id: string;
  userId?: string;
  fullName: string;
  currentCountry: string;
  currentCity: string;
  villageOfOrigin: string;
  yearsInDiaspora: number;
  profession: string;
  organization?: string;
  email: string;
  phone?: string;
  willingToMentor: boolean;
  interestedInInvestment: boolean;
  photoUrl?: string;
  bio?: string;
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
  };
  createdAt: string;
  approved: boolean;
}

export interface DiasporaStory {
  id: string;
  diasporaProfileId: string;
  authorName: string;
  authorLocation: string;
  title: string;
  storyContent: string;
  photoUrls?: string[];
  videoUrl?: string;
  featured: boolean;
  approved: boolean;
  likes: number;
  createdAt: string;
}

export interface DiasporaChapter {
  id: string;
  chapterName: string;
  country: string;
  region: string;
  president: string;
  contactEmail: string;
  contactPhone?: string;
  memberCount: number;
  establishedYear: number;
  logoUrl?: string;
  description: string;
  meetingSchedule?: string;
  websiteUrl?: string;
  socialMedia?: {
    facebook?: string;
    whatsappGroup?: string;
  };
}

// ============================================
// REGISTRATION & CONTESTANT TYPES
// ============================================

export type RegistrationCategory = 
  | 'miss-erue-pageant'
  | 'football-team'
  | 'quiz-competition'
  | 'dance-competition'
  | 'vendor'
  | 'volunteer'
  | 'general-attendance';

export interface EventRegistration {
  id: string;
  registrationCode: string;
  category: RegistrationCategory;
  fullName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female';
  villageOfOrigin: string;
  currentLocation: string;
  isDiaspora: boolean;
  
  pageantData?: {
    height: string;
    talent: string;
    achievements: string;
    platform: string;
    photoUrl: string;
    videoUrl?: string;
  };
  footballData?: {
    teamName: string;
    coachName: string;
    coachPhone: string;
    teamPhotoUrl?: string;
    playerCount: number;
  };
  quizData?: {
    educationalLevel: string;
    institution: string;
    subjectAreas: string[];
  };
  danceData?: {
    danceStyle: string;
    groupOrSolo: 'group' | 'solo';
    groupSize?: number;
    performanceVideoUrl?: string;
  };
  vendorData?: {
    businessName: string;
    productCategory: string;
    boothSizePreference: 'small' | 'medium' | 'large';
    productDescription: string;
  };
  volunteerData?: {
    preferredRoles: string[];
    availableDates: string[];
    hasExperience: boolean;
    experienceDescription?: string;
  };
  
  paymentStatus: 'pending' | 'paid' | 'waived' | 'refunded';
  paymentReference?: string;
  paymentAmount?: number;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  approvalNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  submittedAt: string;
  ticketQrCode?: string;
}

// ============================================
// VOTING SYSTEM TYPES
// ============================================

export interface Contestant {
  id: string;
  contestantNumber: string;
  registrationId: string;
  category: 'miss-erue-pageant' | 'quiz-competition' | 'dance-competition';
  stageName: string;
  fullName: string;
  age: number;
  villageOfOrigin: string;
  bio: string;
  photoUrls: string[];
  videoUrl?: string;
  platformStatement?: string;
  voteCount: number;
  paidVoteCount: number;
  freeVoteCount: number;
  status: 'active' | 'eliminated' | 'winner' | 'runner-up';
  finalPosition?: number;
  createdAt: string;
}

export interface Vote {
  id: string;
  contestantId: string;
  voterEmail?: string;
  voterPhone?: string;
  voterIp: string;
  voteType: 'free' | 'paid';
  voteCount: number;
  paymentReference?: string;
  paymentAmount?: number;
  createdAt: string;
}

// ============================================
// LIVE STREAM TYPES
// ============================================

export interface LiveStreamSession {
  id: string;
  title: string;
  description: string;
  streamPlatform: 'youtube' | 'facebook' | 'twitch' | 'custom';
  streamUrl: string;
  embedUrl: string;
  eventId?: string;
  scheduledStart: string;
  actualStart?: string;
  actualEnd?: string;
  status: 'scheduled' | 'live' | 'ended' | 'cancelled';
  viewerCount: number;
  peakViewers: number;
  thumbnailUrl?: string;
  recordingUrl?: string;
  createdBy: string;
  createdAt: string;
}

// ============================================
// MEDIA GALLERY TYPES
// ============================================

export interface MediaItem {
  id: string;
  title: string;
  description?: string;
  type: 'photo' | 'video' | 'audio' | 'document';
  url: string;
  cloudinaryPublicId?: string;
  thumbnailUrl?: string;
  fileSize?: number;
  duration?: number;
  eventId?: string;
  villageId?: string;
  albumName?: string;
  tags: string[];
  photographer?: string;
  capturedAt?: string;
  isPublic: boolean;
  uploadedBy: string;
  uploadedAt: string;
  viewCount: number;
  downloadCount: number;
}

// ============================================
// CONTACT FORM TYPES
// ============================================

export interface ContactMessage {
  id: string;
  senderName: string;
  senderEmail: string;
  senderPhone?: string;
  senderLocation?: string;
  category: 'general-inquiry' | 'sponsorship' | 'media-press' | 'diaspora' | 'registration' | 'complaint' | 'other';
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'closed' | 'spam';
  assignedTo?: string;
  repliedBy?: string;
  repliedAt?: string;
  replyMessage?: string;
  createdAt: string;
}

// ============================================
// NEWSLETTER TYPES
// ============================================

export interface NewsletterSubscriber {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  villageOfOrigin?: string;
  location?: string;
  isDiaspora: boolean;
  subscribedTo: ('festival-updates' | 'raffle-news' | 'diaspora-briefs' | 'palace-gazette' | 'all')[];
  status: 'active' | 'unsubscribed' | 'bounced';
  subscribedAt: string;
  unsubscribedAt?: string;
  source: 'website' | 'registration' | 'admin-import';
}

// ============================================
// CENTRAL WORKING COMMITTEE (CWC) TYPES
// ============================================

export interface CWCMember {
  id: string;
  fullName: string;
  position: string;
  subcommittee?: string;
  bio: string;
  photoUrl?: string;
  email?: string;
  phone?: string;
  villageOfOrigin?: string;
  displayOrder: number;
  isActive: boolean;
}

// ============================================
// LOCAL ORGANIZING COMMITTEE (LOC) TYPES
// ============================================

export interface LOCMember {
  id: string;
  locId: string;
  fullName: string;
  position: string;
  email?: string;
  phone?: string;
  joinedDate: string;
}

export interface LocalOrganizingCommittee {
  id: string;
  villageId: string;
  villageName: string;
  chairmanName: string;
  chairmanPhone: string;
  chairmanEmail?: string;
  secretaryName?: string;
  secretaryPhone?: string;
  secretaryEmail?: string;
  memberCount: number;
  establishedDate: string;
  status: 'pending' | 'established' | 'active' | 'dissolved';
  notes?: string;
  members?: LOCMember[];
}

// ============================================
// PROMINENT INDIGENES REGISTRY
// ============================================

export interface ProminentIndigene {
  id: string;
  fullName: string;
  title?: string;
  villageOfOrigin: string;
  profession: string;
  organization?: string;
  currentLocation: string;
  isDiaspora: boolean;
  category: 'professional' | 'business' | 'academic' | 'traditional' | 'youth' | 'women' | 'religious' | 'political' | 'other';
  achievements: string;
  phone?: string;
  email?: string;
  photoUrl?: string;
  submittedByVillageHead?: string;
  consented: boolean;
  consulted: boolean;
  consultationNotes?: string;
  createdAt: string;
}

// ─── RBAC Permission Keys ────────────────────────────────────────────────────
export type Permission =
  // Governance
  | 'view_board' | 'board_approve' | 'board_minutes'
  | 'view_cwc'   | 'cwc_budget_edit' | 'cwc_finance_manage'
  | 'view_loc'   | 'loc_budget_input'
  // Finance
  | 'budget_view' | 'budget_edit' | 'budget_approve'
  | 'accounting_view' | 'accounting_edit' | 'finance_manage'
  | 'reports_view' | 'reports_export' | 'raffle_manage'
  // Operations
  | 'programme' | 'registrations' | 'community_admin'
  | 'communications' | 'news_publishing' | 'media'
  | 'documents' | 'voting' | 'user_manage' | 'role_manage'
  // Legacy compat
  | 'accounting' | 'raffle' | 'sponsorship';

// ─── Role → Permission Map ───────────────────────────────────────────────────
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  PLATFORM_ADMIN: [
    'view_board','board_approve','board_minutes',
    'view_cwc','cwc_budget_edit','cwc_finance_manage',
    'view_loc','loc_budget_input',
    'budget_view','budget_edit','budget_approve',
    'accounting_view','accounting_edit','finance_manage',
    'reports_view','reports_export','raffle_manage',
    'programme','registrations','community_admin',
    'communications','news_publishing','media',
    'documents','voting','user_manage','role_manage',
    'accounting','raffle','sponsorship',
  ],
  BOARD_CHAIR: [
    'view_board','board_approve','board_minutes',
    'budget_view','reports_view','reports_export',
  ],
  BOARD_TRUSTEE: [
    'view_board','board_approve','board_minutes',
    'budget_view','reports_view',
  ],
  CWC_TREASURER: [
    'view_cwc','cwc_budget_edit','cwc_finance_manage',
    'budget_view','budget_edit',
    'accounting_view','accounting_edit','finance_manage',
    'reports_view','reports_export','raffle_manage',
    'accounting','raffle',
  ],
  CWC_SECRETARY: [
    'view_cwc','documents','communications','news_publishing',
  ],
  CWC_MEMBER: [
    'view_cwc','budget_view','reports_view',
  ],
  LOC_CHAIR: [
    'view_loc','loc_budget_input','budget_view','reports_view',
  ],
  LOC_MEMBER: [
    'view_loc','loc_budget_input',
  ],
  ACCOUNTANT: [
    'accounting_view','accounting_edit','finance_manage',
    'budget_view','budget_edit',
    'reports_view','reports_export','raffle_manage',
    'accounting','raffle',
  ],
  FESTIVAL_COORDINATOR: [
    'programme','registrations','community_admin',
    'communications','news_publishing','media',
    'budget_view','reports_view','accounting',
  ],
  SECRETARY: [
    'documents','communications','news_publishing',
  ],
  MEDIA_MANAGER: [
    'media','news_publishing','documents',
  ],
  REGISTRAR: [
    'registrations','voting',
  ],
  VIEWER: [],
};

// ─── Helper: resolve all permissions for a user ──────────────────────────────
export function resolvePermissions(
  systemRole: string,
  extraPerms: string[] = []
): Permission[] {
  const base: Permission[] = ROLE_PERMISSIONS[systemRole.toUpperCase()] || [];
  const extra = extraPerms.filter((p): p is Permission =>
    (ROLE_PERMISSIONS.PLATFORM_ADMIN as string[]).includes(p)
  );
  return Array.from(new Set([...base, ...extra]));
}

// ─── Helper: check single permission ─────────────────────────────────────────
export function hasPermission(
  systemRole: string,
  permission: Permission,
  extraPerms: string[] = []
): boolean {
  return resolvePermissions(systemRole, extraPerms).includes(permission);
}




// ============================================================
// DROP 49 — CONTACTS DIRECTORY CRM TYPES
// ============================================================
export type ContactType =
  | 'BOT'
  | 'CWC'
  | 'LOC'
  | 'VILLAGE_REP'
  | 'DIASPORA'
  | 'VENDOR'
  | 'SPONSOR'
  | 'GUEST'
  | 'STAFF'
  | 'MEDIA';

export interface ContactEntry {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
  type: ContactType;
  role?: string;
  village?: string;
  community?: string;
  organization?: string;
  address?: string;
  state?: string;
  country: string;
  isActive: boolean;
  isDiaspora: boolean;
  notes?: string;
  tags: string[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  festivalYear: number;
}

export interface ContactsStats {
  total: number;
  active: number;
  diaspora: number;
  byType: Record<string, number>;
}
// END DROP 49 TYPES
